import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createBucketMovements,
  createOnDemandRequests,
  fetchBucketContracts,
  fetchBucketRequests,
  fetchBucketStays,
  fetchOnDemandContracts,
  fetchOnDemandRequests,
  fetchPresignedPhotoUrl,
  uploadOnDemandPhoto,
  type CreateBucketMovementInput,
  type CreateOnDemandRequestsInput,
  type ListBucketMovementsFilter,
  type ListOnDemandContractsFilter,
  type ListOnDemandRequestsFilter,
} from '@/integrations/agreements/client';

/** Coleta sob demanda no musa-agreements: contratos, criação e "Minhas solicitações". */

const onDemandKeys = {
  all: ['agreements-on-demand'] as const,
  contracts: (filter: ListOnDemandContractsFilter) => [...onDemandKeys.all, 'contracts', filter] as const,
  requests: (filter: ListOnDemandRequestsFilter) => [...onDemandKeys.all, 'requests', filter] as const,
  bucketContracts: (filter: object) => [...onDemandKeys.all, 'bucket-contracts', filter] as const,
  bucketStays: (contractId: number) => [...onDemandKeys.all, 'bucket-stays', contractId] as const,
  bucketRequests: (filter: ListBucketMovementsFilter) => [...onDemandKeys.all, 'bucket-requests', filter] as const,
  presignedPhoto: (url: string) => [...onDemandKeys.all, 'presigned-photo', url] as const,
};

export function useOnDemandContracts(filter: ListOnDemandContractsFilter, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: onDemandKeys.contracts(filter),
    queryFn: () => fetchOnDemandContracts(filter),
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useOnDemandRequests(filter: ListOnDemandRequestsFilter) {
  return useQuery({
    queryKey: onDemandKeys.requests(filter),
    queryFn: () => fetchOnDemandRequests(filter),
    placeholderData: keepPreviousData,
  });
}

/**
 * Cria as solicitações e invalida contratos (o uso do mês mudou) e a listagem. Sem retry: a
 * criação não é idempotente — repetir um POST que chegou duplicaria as solicitações.
 */
export function useCreateOnDemandRequests() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, input }: { contractId: number; input: CreateOnDemandRequestsInput }) =>
      createOnDemandRequests(contractId, input),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: onDemandKeys.all }),
  });
}

export function useUploadOnDemandPhoto() {
  return useMutation({ mutationFn: (file: File) => uploadOnDemandPhoto(file), retry: false });
}

/** 4 minutos: o link assinado vale 5. */
const PRESIGNED_STALE_TIME = 4 * 60 * 1000;

/** Link assinado da foto para exibir (o bucket é privado). */
export function usePresignedPhotoUrl(url: string) {
  return useQuery({
    queryKey: onDemandKeys.presignedPhoto(url),
    queryFn: () => fetchPresignedPhotoUrl(url),
    staleTime: PRESIGNED_STALE_TIME,
    retry: false,
  });
}

export function useBucketContracts(
  filter: { generatorId?: number; page?: number; pageSize?: number },
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: onDemandKeys.bucketContracts(filter),
    queryFn: () => fetchBucketContracts(filter),
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useBucketStays(contractId: number | null) {
  return useQuery({
    queryKey: onDemandKeys.bucketStays(contractId ?? 0),
    queryFn: () => fetchBucketStays(contractId as number),
    enabled: contractId != null,
  });
}

export function useBucketRequests(filter: ListBucketMovementsFilter) {
  return useQuery({
    queryKey: onDemandKeys.bucketRequests(filter),
    queryFn: () => fetchBucketRequests(filter),
    placeholderData: keepPreviousData,
  });
}

/** Sem retry, como a criação de solicitação: o POST não é idempotente. */
export function useCreateBucketMovements() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, inputs }: { contractId: number; inputs: CreateBucketMovementInput[] }) =>
      createBucketMovements(contractId, inputs),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: onDemandKeys.all }),
  });
}
